import React, { FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import { NumericFormField } from '../../../components/FormFields/NumericFormField';
import { LocalSpinner } from '../../../components/LocalSpinner';
import { VehiclePriceTypes } from '../../../resources/base/models/enums';
import { Analytics } from '../../../services/analytics';
import { vehicleSummaryEvent } from '../../../services/analytics/analyticsEvents';
import { getCatalogVehicle } from '../../../services/api/miles/catalogVehicleDetailsPrice';
import { getDetailsPrice } from '../../../services/api/miles/detailsPrice';
import { roundNumber } from '../../../services/common/formatNew';
import { assertDefined } from '../../../services/common/miscUtils';
import { staticSelector } from '../../../services/redux';
import { loadingActions, loadingSelectors } from '../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { QuotationFormData } from '../../../types';

let quoteDetailsPrice: QuoteSchema.Schemas.GetPricingDetailsResponse;
let vehicleDetailsPrice: VehicleIntegration.Components.Schemas.GetCatalogVehicleByCatalogIdResponse;

const getTotalAmountByCostType = (
  costType: 'TRANSPORTATION' | 'DELIVERY' | 'REGISTRATION',
  type: VehiclePriceTypes,
  priceSource:
    | QuoteSchema.Schemas.GetPricingDetailsResponse
    | VehicleIntegration.Components.Schemas.GetCatalogVehicleByCatalogIdResponse
): number => {
  const offers = priceSource?.financialProduct?.offers;

  const totalAmount = offers
    ?.flatMap((offer) => offer.deliveryOptions)
    ?.flatMap((option) => option?.deliveryCostComponents)
    ?.filter((component) => component?.costType === costType)
    ?.map((component) => {
      if (!component?.deliveryCostAmount) {
        return 0;
      }

      const { netAmount } = component.deliveryCostAmount;
      let { grossAmount } = component.deliveryCostAmount;

      if (grossAmount === undefined) {
        grossAmount = netAmount * 1.2;
      }

      return type === VehiclePriceTypes.HT ? netAmount : grossAmount;
    })
    .reduce((acc, amount) => acc + amount, 0);

  return roundNumber(totalAmount || 0);
};

const renderVehiclePrice = (type: VehiclePriceTypes, isDisabled: boolean) => {
  return (
    <Layout>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`preWarehousePrice${type}`}
          labelTranslationKey="step3:vehicleDetails:price:preWarehousePrice"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`transport${type}`}
          labelTranslationKey="step3:vehicleDetails:price:transport"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`priceCatalogue${type}`}
          labelTranslationKey="step3:vehicleDetails:price:priceCatalogue"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`grayCard${type}`}
          labelTranslationKey="step3:vehicleDetails:price:grayCard"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
    </Layout>
  );
};

const getTotalOptionsPrice = (priceType: VehiclePriceTypes, isAccessory = false) => {
  const getPriceAmount = (option: QuoteSchema.Schemas.VehicleOptionGetPricingDetails) => {
    return (
      (priceType === VehiclePriceTypes.HT
        ? option?.optionPriceAmount?.netAmount
        : option?.optionPriceAmount?.grossAmount) ?? 0
    );
  };

  const total = quoteDetailsPrice?.vehicleData?.model?.options
    ?.filter((option) => (isAccessory ? option.type === 'ACCESSORY_ORIGINAL' : !option.type))
    ?.map(getPriceAmount)
    ?.reduce((acc, amount) => acc + amount, 0);

  return roundNumber(total);
};

export const renderVehicleOptions = (type: VehiclePriceTypes, isDisabled: boolean) => {
  return (
    <Layout>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`options${type}`}
          labelTranslationKey="step3:vehicleDetails:options:options"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`accesories${type}`}
          labelTranslationKey="step3:vehicleDetails:options:accesories"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
      <Layout.Item default="1/1">
        <NumericFormField
          fieldName={`deliveryPackage${type}`}
          labelTranslationKey="step3:vehicleDetails:options:deliveryPackage"
          type="money"
          maximumFractionDigits={2}
          additionalLabelText={type}
          isReadonly={!isDisabled}
          isDisabled={isDisabled}
        />
      </Layout.Item>
    </Layout>
  );
};

const fetchVehicleDetails = async () => {
  const milesVehicleId = assertDefined(staticSelector(quoteSelectors.selectMilesVehicleId), 'Get milesVehicleId.');
  vehicleDetailsPrice = await getCatalogVehicle(milesVehicleId);
  const quoteId = assertDefined(staticSelector(quoteSelectors.selectQuoteId), 'Get quoteId.');
  quoteDetailsPrice = await getDetailsPrice(quoteId);
};

export const VehicleDetails: FunctionComponent<{
  isFormDisabled: boolean;
  t: TFunction<'translation', undefined>;
  values: QuotationFormData;
}> = ({ isFormDisabled }) => {
  const isLoading = useSelector(loadingSelectors.selectIsRenderingLocalSpinner);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(loadingActions.setRenderingLocalSpinner(true));
    fetchVehicleDetails()
      .catch(() => {
        navigate.error();
      })
      .finally(() => {
        dispatch(loadingActions.setRenderingLocalSpinner(false));
      });

    Analytics.push(vehicleSummaryEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitialValuesForVehiclesPriceType = (
    type: VehiclePriceTypes,
    vehicleDetailsPrice: VehicleIntegration.Components.Schemas.GetCatalogVehicleByCatalogIdResponse,
    quoteDetailsPrice: QuoteSchema.Schemas.GetPricingDetailsResponse,
    prefix: string
  ) => {
    const transport = getTotalAmountByCostType('TRANSPORTATION', type, vehicleDetailsPrice);
    const priceCatalogue = roundNumber(
      type === VehiclePriceTypes.TTC
        ? vehicleDetailsPrice?.vehicleData?.retailPriceAmount?.grossAmountExclTransportCost || 0
        : vehicleDetailsPrice?.vehicleData?.retailPriceAmount?.netAmountExclTransportCost || 0
    );
    const preWarehousePrice = roundNumber(priceCatalogue - transport);
    return {
      [`preWarehousePrice${prefix}`]: preWarehousePrice,
      [`transport${prefix}`]: transport,
      [`priceCatalogue${prefix}`]: priceCatalogue,
      [`grayCard${prefix}`]: getTotalAmountByCostType('REGISTRATION', type, quoteDetailsPrice),
      [`options${prefix}`]: getTotalOptionsPrice(type),
      [`accesories${prefix}`]: getTotalOptionsPrice(type, true),
      [`deliveryPackage${prefix}`]: getTotalAmountByCostType('DELIVERY', type, quoteDetailsPrice),
    };
  };

  const initialValues: { [x: string]: number } = {
    ...getInitialValuesForVehiclesPriceType(VehiclePriceTypes.HT, vehicleDetailsPrice, quoteDetailsPrice, 'HT'),
    ...getInitialValuesForVehiclesPriceType(VehiclePriceTypes.TTC, vehicleDetailsPrice, quoteDetailsPrice, 'TTC'),
  };

  if (isLoading) {
    return <LocalSpinner fullPage />;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Formik initialValues={initialValues} onSubmit={() => {}}>
      <Form>
        <Layout className="u-mb-small">
          <Layout.Item default="1/4" s="1/1" m="1/2">
            {renderVehiclePrice(VehiclePriceTypes.HT, isFormDisabled)}
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1" m="1/2">
            {renderVehiclePrice(VehiclePriceTypes.TTC, isFormDisabled)}
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1" m="1/2">
            {renderVehicleOptions(VehiclePriceTypes.HT, isFormDisabled)}
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1" m="1/2">
            {renderVehicleOptions(VehiclePriceTypes.TTC, isFormDisabled)}
          </Layout.Item>
        </Layout>
      </Form>
    </Formik>
  );
};
